<template>
  <div v-if="canRead">
    <div class="card col-md-12">
      <div class="card-header" style="margin-bottom: 10px">
        <h4 class="card-title" style="display:inline">
          Street Search
        </h4>
        <button type="button" v-if="canEdit" @click="add" class="btn btn-fill btn-info btn-add-street pull-right">
          Add Street
        </button>
      </div>
      <div class="card-body form-group row">
        <div class="col-md-11">
          <el-input type="search" name="search" placeholder="Search" v-validate="'required'" @keyup.enter.native="search" v-model="query" />
          <small class="text-danger" v-show="errors.has('search')">
            {{ getError('search') }}
          </small>
        </div>
        <div class="col-md-1" style="padding-left: 0; margin-top: -15px;">
          <label>Active</label>
          <p-switch v-model="active" type="primary" on-text="YES" off-text="NO" style="display:block"></p-switch>
        </div>
      </div>
      <div class="card-body form-group">
        <button type="button" @click="search" ref="query" :disabled="isSearchButtonDisabled" class="btn btn-fill btn-info pull-right btn-search">
          Search
        </button>
        <button type="button" @click="clear" :disabled="isClearButtonDisabled" class="btn btn-fill btn-info pull-right btn-search">
          Clear
        </button>
      </div>
    </div><!-- end card -->
    <div class="card">
      <div class="card-body">
        <div class="col-md-12">
          <el-table class="wrap-whole-words indexed-table-column-width"
            header-row-class-name="text-primary"
            :data="tableData"
            :max-height="tableHeight(500)">
            <el-table-column
                :width="this.constants.INDEX_COLUMN_WIDTHS.WIDE"
                align="center"
                label="#"
                type="index" />
            <el-table-column label="Street">
              <template slot-scope="props">
                <span class="invisible">{{ props.row.id }}</span>
                <a href="#" @click.prevent="edit(props.row)">{{ props.row.name }}</a>
              </template>
            </el-table-column>
            <el-table-column
              label="Cities"
              min-width="200">
              <template slot-scope="props">
                {{joinCities(props.row.cities)}}
              </template>
            </el-table-column>
            <el-table-column
              width="105"
              fixed="right"
              label="Actions">
              <template slot-scope="props">
                <p-button type="success" v-if="canRead" size="sm" icon>
                  <i class="fa fa-edit" @click="edit(props.row)"></i>
                </p-button>
                <p-button type="danger" v-if="canDeleteStreet" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import {mapGetters} from 'vuex'
  import {notifySuccess, notifyError, messageForSafeDelete, getTableHeight} from 'src/utilities/utils'
  import _ from 'lodash'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import PButton from 'src/components/UIComponents/Button.vue'
  import Permissions from 'src/utilities/permissions'
  import {OK, NO_CONTENT} from 'http-status-codes'
  import Constants from 'src/utilities/constants'

  export default {
    components: {
      PButton,
      PSwitch
    },
    computed: {
      ...mapGetters({
        tableData: 'streets/results',
      }),
      isClearButtonDisabled() {
        return !this.query && this.tableData.length === 0
      },
      isSearchButtonDisabled() {
        return !this.query
      },
      canRead() {
        return this.$can('read', Permissions.STREETS)
      },
      canEdit() {
        return this.$can('edit', Permissions.STREETS)
      },
      canDeleteStreet() {
        if (this.$store.get('security/isInGroup', Constants.GROUPS.SUPER_USERS)) {
          return true
        }
      }
    },
    data() {
      return {
        toDelete: null,
        query: null,
        active: true,
        constants: Constants
      }
    },
    methods: {
      clear() {
        this.$validator.pause()
        this.query = null
        this.$store.commit('streets/resetResults')
        setTimeout(this.resumeValidation, 250)
      },
      resumeValidation() {
        this.$validator.resume()
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      add() {
        this.$store.commit('streets/resetEdit')
        this.$eventHub.$emit('show-modal-5', 'Street', null, '500px')
      },
      edit(street) {
        this.$eventHub.$emit('show-modal-5', 'Street', {id: street.id}, '500px')
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      async search(optionalStreet) {
        let isSearching = true // refreshing search results
        let query = _.trim(this.query)
        if (optionalStreet.hasOwnProperty('name')) {
          query = optionalStreet.name
          isSearching = false
        }

        // don't fire validation when reloading after adding/editing a street name
        if (!isSearching || (isSearching && await this.$validator.validateAll())) {
          const payload = {
            query: query,
            active: this.active
          }
          const response = await this.$store.dispatch('streets/fetchByNameStartsWith', payload)
          if (OK !== response.status) {
            notifyError(this, 'Error Searching Streets')
          } else if (this.query && response.data.length === 0) {
            this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
          }
        }
      },
      async confirmDelete(street) {
        let message = ''
        this.toDelete = street.id
        let showButton = false

        const responseProjects = await this.$store.dispatch('projects/fetchFindByStreetName', street.name)
        if (OK !== responseProjects.status) {
          notifyError(this, 'Error determining if ' + street.name + ' is in use. Unable to delete street.')
          return
        }

        if (responseProjects.data.length > 0) {
          message = '<strong>' + street.name + '</strong> is used by at least one project and cannot be deleted. You may inactivate it.'
          showButton = false
          this.showConfirmDelete(street, message, showButton)
          return
        }

        message = 'Are you sure you want to delete <strong>' + street.name + '</strong>? This will completely remove the street.'

        const responsePriceRules = await this.$store.dispatch('priceRules/fetchFindByStreetName', street.name)
        const usedByPriceRules = responsePriceRules.data
        if (OK === responsePriceRules.status && usedByPriceRules.length > 0) {
          message = '<strong>' + street.name + '</strong> is used by the following price rules and cannot be deleted:'
          message += '<p>'
          message = messageForSafeDelete(usedByPriceRules, 'shortId', message)
          message += '</p>'
          message += '<p>If you really want to delete this street, you must first remove associations to it in each of the price rules listed above, then come back here and delete the street.</p>'
          showButton = false
          this.showConfirmDelete(street, message, showButton)
          return
        }

        // happy path - street can be deleted
        showButton = true
        this.showConfirmDelete(street, message, showButton)
      },
      showConfirmDelete(street, message, showButton) {
        const payload = {
          message,
          entity: street,
          delete: this.delete,
          showButton,
        }
        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete() {
        const response = await this.$store.dispatch('delete', { endpoint: 'streets', id: this.toDelete })
        if (NO_CONTENT === response.status) {
          this.$store.commit('streets/delete', this.toDelete)
          notifySuccess(this, 'Street Deleted Successfully')
          return
        }
        notifyError(this, 'Error Deleting Street')
      },
      joinCities(cities) {
        return cities.join(', ')
      },
    },
    mounted() {
      this.$eventHub.$on('streetSaved', this.search)
    },
    destroyed() {
      this.$eventHub.$off('streetSaved', this.search)
    }
  }
</script>
<style>
  .btn-add-street {
    margin-left: 15px;
  }
  .btn-search {
    margin-bottom: 15px;
   }
  .results-per-page {
    margin-bottom: 5px;
  }
</style>
